import React from "react";
import { Warning } from "@vibe/icons";
import { changingColumns, isSameName, replacedBySide } from "./mergeItem";
import type {
  MergeColumnChoices,
  MergeDisplayValue,
  MergeNameFrom,
  MergePreview,
  MergeSharedColumn,
} from "./mergeItem.types";

interface MergeItemReviewProps {
  preview: MergePreview;
  nameFrom: MergeNameFrom;
  onNameFromChange: (nameFrom: MergeNameFrom) => void;
  columnChoices: MergeColumnChoices;
  onColumnChoiceChange: (columnKey: string, side: MergeNameFrom) => void;
  onUseAll: (side: MergeNameFrom) => void;
}

/** Fallback when monday reports no colour for a status label, as the item view does. */
const STATUS_FALLBACK_COLOR = "#C4C4C4";

const ROW_GRID = "grid grid-cols-[92px_minmax(0,1fr)_minmax(0,1fr)] gap-x-2 items-center";

const Value = ({ value, type }: { value: MergeDisplayValue | null; type: string }) => {
  const text = value?.text?.trim() ?? "";
  if (!text) {
    return <span className="text-sm text-[var(--secondary-text-color)] italic">Empty</span>;
  }
  if (type === "status") {
    return (
      <span
        className="inline-block max-w-full truncate rounded px-2 py-0.5 text-[13px] text-white"
        style={{ backgroundColor: value?.labelColor ?? STATUS_FALLBACK_COLOR }}
      >
        {text}
      </span>
    );
  }
  return <span className="block truncate text-sm">{text}</span>;
};

/** One selectable side of a row: a radio and the value it would keep. */
const Choice = ({
  group,
  label,
  checked,
  onSelect,
  children,
}: {
  group: string;
  label: string;
  checked: boolean;
  onSelect: () => void;
  children: React.ReactNode;
}) => (
  <label
    className={`flex items-center gap-2 min-w-0 px-2.5 py-1.5 rounded-md cursor-pointer border border-solid ${
      checked
        ? "border-[#0073EA] shadow-[inset_0_0_0_1px_#0073EA] bg-[#F0F7FF]"
        : "border-[#D0D4E4] bg-white opacity-60 hover:opacity-100"
    }`}
  >
    <input type="radio" name={group} aria-label={label} checked={checked} onChange={onSelect} className="m-0 shrink-0" />
    <span className="min-w-0 flex-1">{children}</span>
  </label>
);

const ChoiceRow = ({
  title,
  group,
  side,
  onSelect,
  current,
  other,
  currentLabel,
  otherLabel,
}: {
  title: string;
  group: string;
  side: MergeNameFrom;
  onSelect: (side: MergeNameFrom) => void;
  current: React.ReactNode;
  other: React.ReactNode;
  currentLabel: string;
  otherLabel: string;
}) => (
  <li className={`${ROW_GRID} px-2.5 py-1.5 border-0 border-b border-solid border-[#E7E9EF] last:border-b-0`}>
    <span className="text-sm font-medium truncate" title={title}>
      {title}
    </span>
    <Choice group={group} label={`${title}: ${currentLabel}`} checked={side === "current"} onSelect={() => onSelect("current")}>
      {current}
    </Choice>
    <Choice group={group} label={`${title}: ${otherLabel}`} checked={side === "other"} onSelect={() => onSelect("other")}>
      {other}
    </Choice>
  </li>
);

const FixedRow = ({ column }: { column: MergeSharedColumn }) => (
  <li className={`${ROW_GRID} px-2.5 py-2 border-0 border-b border-solid border-[#E7E9EF] last:border-b-0 text-sm text-[var(--secondary-text-color)]`}>
    <span className="truncate" title={column.title}>
      {column.title}
    </span>
    <span className="col-span-2 flex items-center gap-2 min-w-0 px-2.5">
      {column.same ? (
        <>
          <span className="min-w-0 truncate">
            <Value value={column.current} type={column.type} />
          </span>
          <span className="shrink-0 text-xs">· same on both</span>
        </>
      ) : (
        <span className="text-xs">Can't be copied by SIMB; each item keeps its own</span>
      )}
    </span>
  </li>
);

const joinNames = (names: string[]) => names.join(", ");

/**
 * Step 2 (spec §3, M14): for each shared column that differs, and for the name, the user picks
 * which item's value both items keep. Columns that already match, or that SIMB can't write, are
 * listed without a choice.
 */
const MergeItemReview = ({
  preview,
  nameFrom,
  onNameFromChange,
  columnChoices,
  onColumnChoiceChange,
  onUseAll,
}: MergeItemReviewProps) => {
  const { current, other, shared, notShared, groupBoards } = preview;
  const choosable = changingColumns(preview);
  const fixed = shared.filter((column) => column.same || !column.writable);
  const namesMatch = isSameName(current.name, other.name);
  const differ = choosable.length + (namesMatch ? 0 : 1);
  const { onOther, onCurrent } = replacedBySide(preview, nameFrom, columnChoices);
  const groupBoardNames = groupBoards.map((board) => board.boardName || "a private board");
  const currentLabel = `${current.boardName} (this item)`;

  return (
    <div className="flex flex-col gap-4">
      <p className="m-0 text-sm text-[var(--secondary-text-color)]">
        For each column, pick the value both items will have after the merge.
      </p>

      <section className="flex flex-col gap-1.5">
        <div className="flex justify-between items-baseline">
          <h3 className="m-0 text-sm font-semibold">Shared columns</h3>
          <span className="text-xs text-[var(--secondary-text-color)]">
            {differ} differ · {fixed.length} {fixed.length === 1 ? "has" : "have"} no choice
          </span>
        </div>

        {differ > 0 && (
          <div className={`${ROW_GRID} px-2.5 text-xs font-semibold text-[var(--secondary-text-color)] uppercase`}>
            <span>Column</span>
            <span className="flex justify-between gap-1 min-w-0 px-2.5">
              <span className="truncate">{current.boardName} · this item</span>
              <button
                type="button"
                className="shrink-0 bg-transparent border-0 p-0 cursor-pointer text-xs font-medium normal-case text-[#0060B9] hover:underline"
                onClick={() => onUseAll("current")}
              >
                Use all
              </button>
            </span>
            <span className="flex justify-between gap-1 min-w-0 px-2.5">
              <span className="truncate">{other.boardName}</span>
              <button
                type="button"
                className="shrink-0 bg-transparent border-0 p-0 cursor-pointer text-xs font-medium normal-case text-[#0060B9] hover:underline"
                onClick={() => onUseAll("other")}
              >
                Use all
              </button>
            </span>
          </div>
        )}

        {shared.length === 0 && namesMatch ? (
          <p className="m-0 text-sm text-[var(--secondary-text-color)]">
            These boards have no columns with the same name and type, and the names already match. The items are only
            linked.
          </p>
        ) : (
          <ul className="list-none m-0 p-0 border border-solid border-[#D0D4E4] rounded-md">
            {!namesMatch && (
              <ChoiceRow
                title="Name"
                group="merge-name"
                side={nameFrom}
                onSelect={onNameFromChange}
                current={<span className="block truncate text-sm">{current.name}</span>}
                other={<span className="block truncate text-sm">{other.name}</span>}
                currentLabel={currentLabel}
                otherLabel={other.boardName}
              />
            )}
            {choosable.map((column) => (
              <ChoiceRow
                key={column.columnKey}
                title={column.title}
                group={`merge-column-${column.columnKey}`}
                side={columnChoices[column.columnKey] ?? "current"}
                onSelect={(side) => onColumnChoiceChange(column.columnKey, side)}
                current={<Value value={column.current} type={column.type} />}
                other={<Value value={column.other} type={column.type} />}
                currentLabel={currentLabel}
                otherLabel={other.boardName}
              />
            ))}
            {fixed.map((column) => (
              <FixedRow key={column.columnKey} column={column} />
            ))}
          </ul>
        )}
        {choosable.some((column) => column.defaultFrom === "other") && (
          <span className="text-xs text-[var(--secondary-text-color)]">
            Where this item is empty, {other.boardName}'s value is picked to start with.
          </span>
        )}
      </section>

      {groupBoards.length > 0 && (
        <div
          role="note"
          className="flex gap-2.5 items-start px-3.5 py-3 rounded-md bg-[#E6F1FD] border border-solid border-[#A9CDF5] text-sm"
        >
          <Warning aria-hidden size={18} className="shrink-0 mt-px text-[#0060B9]" />
          <span>
            This item is already synced with <strong>{joinNames(groupBoardNames)}</strong>.{" "}
            {onCurrent.length > 0 ? (
              <>
                Values you take from <strong>{other.boardName}</strong> ({joinNames(onCurrent)}) change there too, not
                only on this item.
              </>
            ) : (
              <>Nothing changes there, since every value comes from this item.</>
            )}{" "}
            {other.boardName} joins that sync.
          </span>
        </div>
      )}

      {notShared.length > 0 && (
        <section className="flex flex-col gap-2">
          <div className="flex justify-between items-baseline">
            <h3 className="m-0 text-sm font-semibold">Not shared, stay as they are</h3>
            <span className="text-xs text-[var(--secondary-text-color)]">
              {notShared.length} {notShared.length === 1 ? "column" : "columns"}
            </span>
          </div>
          <ul className="list-none m-0 p-0 border border-solid border-[#D0D4E4] rounded-md">
            {notShared.map((column) => (
              <li
                key={`${column.boardId}:${column.title}:${column.type}`}
                className="flex items-center justify-between gap-2.5 px-3 py-2.5 border-0 border-b border-solid border-[#E7E9EF] last:border-b-0 text-sm"
              >
                <span className="truncate">{column.title}</span>
                <span className="shrink-0 text-xs text-[var(--secondary-text-color)] bg-[#F6F7FB] rounded-full px-2 py-0.5">
                  Only on {column.boardName}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="m-0 text-[13px] text-[var(--secondary-text-color)]">
        Subitems and updates are not merged. Each item keeps its own. New ones are shared from now on.
      </p>

      <div
        role="alert"
        className="flex gap-2.5 items-start px-3.5 py-3 rounded-md bg-[#FFF4E5] border border-solid border-[#FDAB3D] text-sm"
      >
        <Warning aria-hidden size={18} className="shrink-0 mt-px text-[#B25C00]" />
        {onOther.length + onCurrent.length === 0 ? (
          <span>No values change; the two items are only linked.</span>
        ) : (
          <span className="flex flex-col gap-0.5">
            <span>These values will be replaced. This can't be undone.</span>
            {onOther.length > 0 && (
              <span>
                <strong>{other.boardName}:</strong> {joinNames(onOther)}
              </span>
            )}
            {onCurrent.length > 0 && (
              <span>
                <strong>{joinNames([current.boardName, ...groupBoardNames])}:</strong> {joinNames(onCurrent)}
              </span>
            )}
          </span>
        )}
      </div>
    </div>
  );
};

export default MergeItemReview;
